import { useMemo, useState, useSyncExternalStore } from 'react';
import { countryName } from '../booking/countries';
import { LOCALES } from '../booking/messages';
import { factDisplay, factLabel } from '../booking/scope';
import { formatDateTime } from '../booking/time';
import { questionPriority } from '../interview/model';
import { OpenPoints, ReportSections, SystemsTable } from '../interview/InterviewPage';
import { buildReport, DOMAIN_LABELS } from '../interview/report';
import { DEMO_ENGAGEMENT_ID, type EngagementRecord, type EngagementStatus, type ImplementationStore } from '../store';

/**
 * Back-office AYUMIA des entretiens d'implémentation : la liste des rendez-vous, puis pour chacun
 * les informations collectées (formulaire, préparation, pré-découverte), le compte rendu de
 * l'entretien, la conversation et le détail du moteur (POC §10.9 : « not intended for normal
 * customers »). Démonstration locale : il lit le magasin en mémoire du navigateur. En production,
 * il sera réservé aux équipes AYUMIA authentifiées (permission `implementation.conduct`, et
 * `implementation.debug` pour l'onglet Moteur), et lira l'API.
 */

const STATUS_LABELS: Readonly<Record<EngagementStatus, string>> = {
  scheduled: 'Planifié',
  preparing: 'En préparation',
  ready: 'Prêt',
  in_progress: 'Entretien en cours',
  completed: 'Terminé',
  cancelled: 'Annulé',
};

const FILTERS: ReadonlyArray<{ key: 'all' | 'upcoming' | 'in_progress' | 'completed'; label: string; accept: (status: EngagementStatus) => boolean }> = [
  { key: 'all', label: 'Tous', accept: () => true },
  { key: 'upcoming', label: 'À venir', accept: (status) => status === 'scheduled' || status === 'preparing' || status === 'ready' },
  { key: 'in_progress', label: 'En cours', accept: (status) => status === 'in_progress' },
  { key: 'completed', label: 'Terminés', accept: (status) => status === 'completed' },
];

const SOURCE_LABELS: Readonly<Record<string, string>> = {
  official_registry: 'Registre officiel',
  company_website: 'Site web',
  booking_form: 'Formulaire',
  interview: 'Entretien',
  customer_correction: 'Correction du client',
  ai_inference: 'Déduction IA',
};

const PREPARATION_LABELS: Readonly<Record<string, string>> = { file: 'Fichier', system: 'Système', note: 'Information', skipped: 'Étape passée' };

function localeLabel(code: string): string {
  return LOCALES.find((entry) => entry.code === code)?.label ?? code;
}

function useStore(store: ImplementationStore): number {
  return useSyncExternalStore(store.subscribe, store.snapshot, store.snapshot);
}

export function BackOfficePage({ store, engagementId }: { store: ImplementationStore; engagementId?: string }) {
  useStore(store);
  const record = engagementId === undefined ? undefined : store.get(engagementId);
  return (
    <main className="ay-backoffice" aria-labelledby="ay-backoffice-title">
      <p className="ay-booking__brand">AYUMIA · Back-office</p>
      <p className="ay-interview__demo">Démonstration locale : données fictives ou saisies dans ce navigateur. En production, cet espace est réservé aux équipes AYUMIA authentifiées.</p>
      {engagementId === undefined ? <EngagementList store={store} /> : record === undefined ? <NotFound /> : <EngagementDetail record={record} />}
    </main>
  );
}

function NotFound() {
  return (
    <>
      <h1 id="ay-backoffice-title">Rendez-vous introuvable</h1>
      <p>
        <a href="#/ayumia">Retour à la liste des rendez-vous</a>
      </p>
    </>
  );
}

function EngagementList({ store }: { store: ImplementationStore }) {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['key']>('all');
  const [search, setSearch] = useState('');
  const all = store.list();
  const active = FILTERS.find((entry) => entry.key === filter)!;
  const rows = all.filter((record) => active.accept(record.status) && record.engagement.company.legal_name.toLowerCase().includes(search.trim().toLowerCase()));
  return (
    <>
      <h1 id="ay-backoffice-title">Rendez-vous d’implémentation</h1>
      <div className="ay-backoffice__toolbar">
        <div className="ay-booking__choices" role="group" aria-label="Filtrer par état">
          {FILTERS.map((entry) => (
            <button key={entry.key} type="button" className={`ay-booking__choice${filter === entry.key ? ' ay-booking__choice--active' : ''}`} aria-pressed={filter === entry.key} onClick={() => setFilter(entry.key)}>
              {entry.label} ({all.filter((record) => entry.accept(record.status)).length})
            </button>
          ))}
        </div>
        <label className="ay-field ay-field--inline">
          <span className="ay-visually-hidden">Rechercher une entreprise</span>
          <input type="search" placeholder="Rechercher une entreprise…" value={search} onChange={(event) => setSearch(event.target.value)} />
        </label>
      </div>
      {rows.length === 0 ? (
        <p className="ay-text--muted">Aucun rendez-vous.</p>
      ) : (
        <div className="ay-table-wrap">
          <table className="ay-table">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Entreprise</th>
                <th scope="col">Pays</th>
                <th scope="col">Contact</th>
                <th scope="col">Langue</th>
                <th scope="col">Domaines visés</th>
                <th scope="col">Consultant</th>
                <th scope="col">État</th>
                <th scope="col">Préparation</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((record) => {
                const { engagement } = record;
                const domains = engagement.pre_discovery.facts.find((fact) => fact.key === 'business_scope.target_domains');
                return (
                  <tr key={engagement.engagement_id}>
                    <td>{formatDateTime(engagement.interview.scheduled_at, 'Europe/Paris', 'fr-FR')}</td>
                    <td>
                      <a href={`#/ayumia/${engagement.engagement_id}`}>{engagement.company.legal_name}</a>
                    </td>
                    <td>{countryName(engagement.company.country, 'fr')}</td>
                    <td>
                      {engagement.primary_contact.name}
                      {engagement.primary_contact.role === undefined ? '' : ` · ${engagement.primary_contact.role}`}
                    </td>
                    <td>{localeLabel(engagement.interview.locale)}</td>
                    <td>{domains === undefined ? '—' : factDisplay(domains, 'fr', ', ')}</td>
                    <td>{record.consultant_requested ? 'Demandé' : '—'}</td>
                    <td>
                      <span className={`ay-badge${record.status === 'completed' ? ' ay-badge--success' : ''}`}>{STATUS_LABELS[record.status]}</span>
                    </td>
                    <td>{record.interview === undefined ? '—' : `${record.interview.readiness.overall} %`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

type DetailTab = 'collected' | 'report' | 'conversation' | 'engine';

function EngagementDetail({ record }: { record: EngagementRecord }) {
  const [tab, setTab] = useState<DetailTab>('collected');
  const { engagement } = record;
  const tabs: Array<[DetailTab, string]> = [
    ['collected', 'Informations collectées'],
    ['report', 'Compte rendu'],
    ['conversation', 'Conversation'],
    ['engine', 'Moteur'],
  ];
  const exportReport = (): void => {
    const payload = { engagement, status: record.status, consultant_requested: record.consultant_requested, preparation: record.preparation, report: record.interview === undefined ? null : buildReport(record.interview) };
    const url = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `compte-rendu-${engagement.engagement_id}.json`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  return (
    <>
      <p>
        <a href="#/ayumia">← Tous les rendez-vous</a>
      </p>
      <div className="ay-backoffice__heading">
        <div>
          <h1 id="ay-backoffice-title">{engagement.company.legal_name}</h1>
          <p className="ay-text--muted">
            {formatDateTime(engagement.interview.scheduled_at, 'Europe/Paris', 'fr-FR')} · {engagement.primary_contact.name} · {localeLabel(engagement.interview.locale)}
            {record.consultant_requested ? ' · consultant demandé' : ''}
          </p>
        </div>
        <div className="ay-stack">
          <span className={`ay-badge${record.status === 'completed' ? ' ay-badge--success' : ''}`}>{STATUS_LABELS[record.status]}</span>
          {engagement.engagement_id === DEMO_ENGAGEMENT_ID && record.status !== 'completed' && (
            <a className="ay-button ay-button--secondary" href="#/entretien">
              {record.status === 'in_progress' ? 'Rejoindre l’entretien' : 'Ouvrir l’entretien'}
            </a>
          )}
          <button type="button" className="ay-button ay-button--secondary" onClick={exportReport}>
            Exporter (JSON)
          </button>
        </div>
      </div>
      <div className="ay-interview__tabs ay-backoffice__tabs" role="tablist" aria-label="Sections du rendez-vous">
        {tabs.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} className="ay-interview__tab" onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>
      <div role="tabpanel" aria-label={tabs.find(([key]) => key === tab)![1]} className="ay-backoffice__panel">
        {tab === 'collected' && <Collected record={record} />}
        {tab === 'report' && <Report record={record} />}
        {tab === 'conversation' && <Conversation record={record} />}
        {tab === 'engine' && <Engine record={record} />}
      </div>
    </>
  );
}

function Field({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value === undefined || value === '' ? '—' : value}</dd>
    </div>
  );
}

function Collected({ record }: { record: EngagementRecord }) {
  const { engagement } = record;
  const registration = engagement.company.registration;
  const found = (record.interview?.items ?? []).filter((item) => item.source === 'official_registry' || item.source === 'company_website');
  return (
    <div className="ay-backoffice__cards">
      <section className="ay-card" aria-labelledby="ay-bo-company">
        <h2 id="ay-bo-company">Entreprise</h2>
        <dl className="ay-report__lines">
          <Field label="Raison sociale" value={engagement.company.legal_name} />
          <Field label="Pays" value={countryName(engagement.company.country, 'fr')} />
          <Field label="Identifiant" value={registration === undefined ? undefined : `${registration.value} (${registration.scheme})`} />
          <Field label="Site web" value={engagement.company.website} />
        </dl>
      </section>
      <section className="ay-card" aria-labelledby="ay-bo-contact">
        <h2 id="ay-bo-contact">Contact et rendez-vous</h2>
        <dl className="ay-report__lines">
          <Field label="Contact" value={engagement.primary_contact.name} />
          <Field label="Fonction" value={engagement.primary_contact.role} />
          <Field label="E-mail" value={engagement.primary_contact.email} />
          <Field label="Langue" value={localeLabel(engagement.interview.locale)} />
          <Field label="Fuseau du client" value={engagement.interview.timezone} />
          <Field label="Réservé le" value={formatDateTime(record.booked_at, 'Europe/Paris', 'fr-FR')} />
          <Field label="Consultant" value={record.consultant_requested ? 'Demandé par le client' : 'Non demandé'} />
        </dl>
      </section>
      <section className="ay-card" aria-labelledby="ay-bo-declared">
        <h2 id="ay-bo-declared">Déclaré au rendez-vous</h2>
        <dl className="ay-report__lines">
          {engagement.pre_discovery.facts.map((fact) => (
            <div key={fact.key}>
              <dt>{factLabel(fact, 'fr') ?? 'Objectif principal'}</dt>
              <dd>
                {factDisplay(fact, 'fr', ', ')} <span className="ay-badge">Déclaré</span>
                {fact.locale !== 'fr' && <span className="ay-text--muted"> (répondu en {localeLabel(fact.locale)})</span>}
              </dd>
            </div>
          ))}
          {engagement.declared_context.primary_objective !== undefined && !engagement.pre_discovery.facts.some((fact) => fact.key === 'business_scope.primary_objective') && (
            <Field label="Objectif principal" value={engagement.declared_context.primary_objective} />
          )}
        </dl>
      </section>
      <section className="ay-card" aria-labelledby="ay-bo-preparation">
        <h2 id="ay-bo-preparation">Aide à la préparation</h2>
        {record.preparation.length === 0 ? (
          <p className="ay-text--muted">Rien de transmis.</p>
        ) : (
          <ul className="ay-booking__added">
            {record.preparation.map((entry, index) => (
              <li key={index}>
                <span className="ay-badge">{PREPARATION_LABELS[entry.kind]}</span> {entry.label} <span className="ay-text--muted">· {formatDateTime(entry.at, 'Europe/Paris', 'fr-FR')}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="ay-card" aria-labelledby="ay-bo-prediscovery">
        <h2 id="ay-bo-prediscovery">Trouvé avant l’entretien</h2>
        {found.length === 0 ? (
          <p className="ay-text--muted">Pré-découverte non lancée (registre et site web simulés dans le POC).</p>
        ) : (
          <dl className="ay-report__lines">
            {found.map((item) => (
              <div key={item.id}>
                <dt>{item.label}</dt>
                <dd>
                  {item.value ?? '—'} <span className="ay-text--muted">· {SOURCE_LABELS[item.source ?? ''] ?? item.source}</span>
                </dd>
              </div>
            ))}
          </dl>
        )}
      </section>
    </div>
  );
}

function Report({ record }: { record: EngagementRecord }) {
  const report = useMemo(() => (record.interview === undefined ? null : buildReport(record.interview)), [record.interview]);
  if (report === null) return <p className="ay-text--muted">L’entretien n’a pas encore eu lieu.</p>;
  return (
    <div className="ay-backoffice__cards">
      <section className="ay-card ay-backoffice__wide" aria-labelledby="ay-bo-org">
        <h2 id="ay-bo-org">Organisation et responsabilités</h2>
        {report.organization.length === 0 ? (
          <p className="ay-text--muted">Pas encore abordé.</p>
        ) : (
          <dl className="ay-report__lines">
            {report.organization.map((entry) => (
              <Field key={entry.id} label={entry.label} value={entry.status === 'confirmed' ? entry.value : `${entry.value} (${entry.status === 'inferred' ? 'déduit' : 'à confirmer'})`} />
            ))}
          </dl>
        )}
      </section>
      <section className="ay-card" aria-labelledby="ay-bo-readiness">
        <h2 id="ay-bo-readiness">Préparation : {report.readiness.overall} %</h2>
        <ul className="ay-review__readiness">
          {report.readiness.domains.map((domain) => (
            <li key={domain.label}>
              {domain.label} : {domain.score} %{domain.blocking.length > 0 ? ` (bloquant : ${domain.blocking.join(', ')})` : ''}
            </li>
          ))}
        </ul>
      </section>
      <section className="ay-card" aria-labelledby="ay-bo-scope">
        <h2 id="ay-bo-scope">Périmètre proposé</h2>
        <ul className="ay-review__chips">
          {report.proposed_scope.map((label) => (
            <li key={label} className="ay-badge">
              AYUMIA {label}
            </li>
          ))}
        </ul>
      </section>
      <section className="ay-card ay-backoffice__wide" aria-labelledby="ay-bo-systems">
        <h2 id="ay-bo-systems">Systèmes existants</h2>
        <SystemsTable report={report} />
      </section>
      <section className="ay-card" aria-labelledby="ay-bo-open">
        <h2 id="ay-bo-open">Points ouverts</h2>
        <OpenPoints report={report} />
      </section>
      <section className="ay-card" aria-labelledby="ay-bo-inferred">
        <h2 id="ay-bo-inferred">À faire confirmer</h2>
        {report.inferred_to_confirm.length === 0 ? (
          <p className="ay-text--muted">Rien.</p>
        ) : (
          <ul>
            {report.inferred_to_confirm.map((entry) => (
              <li key={entry.id}>
                {entry.label} : {entry.value} ({entry.status === 'inferred' ? 'déduit par l’IA' : entry.status === 'conflicting' ? 'contradictoire' : 'à confirmer'})
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="ay-card ay-backoffice__wide" aria-labelledby="ay-bo-understood">
        <h2 id="ay-bo-understood">Informations collectées pendant l’entretien</h2>
        <ReportSections report={report} exclude={['organization']} />
      </section>
    </div>
  );
}

function Conversation({ record }: { record: EngagementRecord }) {
  const messages = record.interview?.messages ?? [];
  if (messages.length === 0) return <p className="ay-text--muted">Aucun échange.</p>;
  const author = (role: string): string => (role === 'ai' ? 'AYUMIA' : role === 'consultant' ? 'Consultant' : role === 'system' ? 'Système' : 'Client');
  return (
    <ol className="ay-transcript">
      {messages.map((message) => (
        <li key={message.id} className={`ay-transcript__entry ay-transcript__entry--${message.role}`}>
          <span className="ay-chat__author">
            {author(message.role)} · {formatDateTime(message.at, 'Europe/Paris', 'fr-FR')}
          </span>
          <p>{message.text}</p>
        </li>
      ))}
    </ol>
  );
}

function Engine({ record }: { record: EngagementRecord }) {
  const state = record.interview;
  if (state === undefined) return <p className="ay-text--muted">Le moteur n’a pas encore été sollicité.</p>;
  const candidates = [...(state.current_question?.candidates ?? [])].sort((a, b) => questionPriority(b) - questionPriority(a));
  return (
    <div className="ay-backoffice__cards">
      <section className="ay-card ay-backoffice__wide" aria-labelledby="ay-bo-telemetry">
        <h2 id="ay-bo-telemetry">Fournisseur et consommation</h2>
        <p>
          Fournisseur : {state.telemetry.provider} · modèle : {state.telemetry.model} · appels : {state.telemetry.calls} · jetons entrée / sortie : {state.telemetry.input_tokens} / {state.telemetry.output_tokens} · latence cumulée :{' '}
          {state.telemetry.latency_ms} ms
        </p>
      </section>
      <section className="ay-card ay-backoffice__wide" aria-labelledby="ay-bo-candidates">
        <h2 id="ay-bo-candidates">Questions candidates du tour en cours</h2>
        {candidates.length === 0 ? (
          <p className="ay-text--muted">Aucune question en cours.</p>
        ) : (
          <div className="ay-table-wrap">
            <table className="ay-table">
              <thead>
                <tr>
                  <th scope="col">Question</th>
                  <th scope="col">Gain</th>
                  <th scope="col">Importance</th>
                  <th scope="col">Impact</th>
                  <th scope="col">Incertitude</th>
                  <th scope="col">Dépendance</th>
                  <th scope="col">Redondance</th>
                  <th scope="col">Fatigue</th>
                  <th scope="col">Priorité</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((candidate) => (
                  <tr key={candidate.id} className={candidate.id === state.current_question?.candidate.id ? 'ay-table__row--selected' : undefined}>
                    <td>{candidate.text}</td>
                    <td>{candidate.information_gain}</td>
                    <td>{candidate.importance}</td>
                    <td>{candidate.impact}</td>
                    <td>{candidate.uncertainty}</td>
                    <td>{candidate.dependency}</td>
                    <td>{candidate.redundancy}</td>
                    <td>{candidate.fatigue}</td>
                    <td>
                      <strong>{questionPriority(candidate)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="ay-card ay-backoffice__wide" aria-labelledby="ay-bo-readiness-detail">
        <h2 id="ay-bo-readiness-detail">Calcul de la readiness</h2>
        <ul>
          {state.readiness.domains.map((domain) => (
            <li key={domain.domain}>
              <strong>
                {DOMAIN_LABELS[domain.domain]} {domain.score} %
              </strong>{' '}
              — couvert : {domain.covered.join(', ') || '—'} ; partiel : {domain.partial.join(', ') || '—'} ; manquant : {domain.missing.join(', ') || '—'}
              {domain.blocking.length > 0 ? ` ; bloquant : ${domain.blocking.join(', ')}` : ''}
            </li>
          ))}
        </ul>
      </section>
      <section className="ay-card ay-backoffice__wide" aria-labelledby="ay-bo-raw">
        <h2 id="ay-bo-raw">État brut</h2>
        <pre className="ay-debug__json">{JSON.stringify({ items: state.items, recommendations: state.recommendations }, null, 2)}</pre>
      </section>
    </div>
  );
}
